import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import api from "../lib/api";

const FALLBACK_ROOMS = [
  {
    id: "travel-tourism",
    image_data: "https://customer-assets.emergentagent.com/job_point-ov-view/artifacts/y2uqrobr_IMG_2775.jpeg",
    field: "Travel & Tourism",
    location: "Industry Gala",
    title: "Where destinations get sold before the brochure is printed.",
    story:
      "A working evening with one of our deepest hospitality relationships — the kind that gets operators, brokers, and destinations into the same room and moves them in the same direction. Travel deals close where trust does, and we make sure the right hands meet over the right table.",
    outcome: [
      "Destination strategy advanced",
      "Cross-market partnership in motion",
      "Ongoing advisory role",
    ],
    tag: "Hospitality engagement",
  },
  {
    id: "marketing",
    image_data: "https://customer-assets.emergentagent.com/job_point-ov-view/artifacts/jle2p55h_632D7C93-AFC8-4FD5-9770-BDEDD7551172.png",
    field: "Marketing",
    location: "Brand Reception",
    title: "Marketing isn't a deck. It's who shows up — and why.",
    story:
      "We work with operators who are done playing small in their own market. This was an evening with a marketing partner whose brand we helped sharpen into a louder, clearer, harder-to-ignore voice — and into rooms they used to wait outside of.",
    outcome: [
      "Repositioned for the next decade",
      "Visibility multiplied without volume",
      "Decisive new market posture",
    ],
    tag: "Brand & marketing engagement",
  },
  {
    id: "policy-2",
    image_data: "https://customer-assets.emergentagent.com/job_point-ov-view/artifacts/h6obyy1n_93243CB4-BB60-4B77-B71E-52A11FA8276B.png",
    field: "Government Policy",
    location: "Policy Reception",
    title: "Sitting with the people who can move the rules.",
    story:
      "Policy is a long game played in short rooms. We work those rooms — connecting operators with the elected officials, advisors, and senior staff who actually shape what gets written into law. The photograph is incidental. The conversation is the point.",
    outcome: [
      "Stakeholder coalition aligned",
      "Direct line to decision-makers",
      "Ongoing advisory engagement",
    ],
    tag: "Public-sector engagement",
  },
  {
    id: "policy-3",
    image_data: "https://customer-assets.emergentagent.com/job_point-ov-view/artifacts/bgmbmehy_E5647879-C9B5-4776-8A13-88CA06969E14.png",
    field: "Government Policy",
    location: "Civic Leadership Forum",
    title: "The room where civic momentum meets private capital.",
    story:
      "A working night alongside civic leaders, operators, and community stewards — the rooms where public momentum and private capital have to figure out how to move together. We were there to keep both sides pointed at the outcome, not the optics.",
    outcome: [
      "Cross-sector alignment built",
      "Civic-private bridge established",
      "Continued strategic role",
    ],
    tag: "Public-sector engagement",
  },
];

export default function Confidants() {
  const [rooms, setRooms] = useState(FALLBACK_ROOMS);
  const [activeId, setActiveId] = useState(FALLBACK_ROOMS[0].id);

  useEffect(() => {
    api.get("/gallery", { params: { gallery: "rooms" } })
      .then(({ data }) => {
        if (data && data.length) {
          setRooms(data);
          setActiveId(data[0].id);
        }
      })
      .catch(() => {});
  }, []);

  const active = rooms.find((r) => r.id === activeId) || rooms[0];
  const idx = rooms.findIndex((r) => r.id === activeId);
  const go = (dir) => {
    const next = (idx + dir + rooms.length) % rooms.length;
    setActiveId(rooms[next].id);
  };

  return (
    <section
      id="rooms"
      className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24"
      data-testid="confidants-section"
    >
      <div className="grid grid-cols-12 gap-6 items-end mb-12">
        <div className="col-span-12 md:col-span-7">
          <p className="overline">In The Room</p>
          <h2 className="font-display font-light text-4xl lg:text-5xl tracking-tight mt-4">
            The people we <br />
            <span className="italic text-[var(--pov-cyan)]">sit with.</span>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-5 text-white/60 text-sm md:text-right">
          A practice is measured by the rooms it earns access to. These are a few of ours — moments
          when strategy stopped being a slide and started moving capital, contracts, and outcomes.
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Featured image */}
        <div className="col-span-12 lg:col-span-7 relative">
          <div className="relative aspect-[4/3] lg:aspect-[5/4] overflow-hidden rounded-sm glass">
            <img
              key={active.id}
              src={active.image_data}
              alt={active.title}
              className="absolute inset-0 w-full h-full object-cover fade-up"
              style={{ objectPosition: active.image_position || "50% 50%" }}
              data-testid={`room-image-${active.id}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/30 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#030712]/60 via-transparent to-transparent" />
            <div className="absolute left-6 top-6 pointer-events-none">
              <span className="px-2.5 py-1 text-xs rounded-sm glass-strong text-[var(--pov-cyan)] uppercase tracking-wider">
                {active.field}
              </span>
            </div>
            {(active.location || active.event_date) ? (
              <div className="absolute right-6 top-6 flex flex-wrap items-center gap-2 justify-end pointer-events-none">
                {active.location ? (
                  <span className="px-2.5 py-1 text-xs rounded-sm glass-strong text-white/70">
                    {active.location}
                  </span>
                ) : null}
                {active.event_date ? (
                  <span className="px-2.5 py-1 text-xs rounded-sm glass-strong text-white/50">
                    {active.event_date}
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="absolute left-6 bottom-6 right-6">
              <p className="font-display text-2xl lg:text-3xl tracking-tight max-w-xl">
                {active.title}
              </p>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="mt-4 grid grid-cols-4 gap-3" data-testid="room-thumbs">
            {rooms.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setActiveId(r.id)}
                data-testid={`room-thumb-${r.id}`}
                aria-pressed={r.id === activeId}
                className={`relative aspect-[4/3] overflow-hidden rounded-sm transition-all duration-300 ${
                  r.id === activeId
                    ? "ring-2 ring-[var(--pov-coral)] shadow-[0_0_24px_rgba(255,107,74,0.35)]"
                    : "ring-1 ring-white/10 hover:ring-white/40 opacity-70 hover:opacity-100"
                }`}
              >
                <img src={r.image_data} alt={r.title} className="w-full h-full object-cover" />
                <div className={`absolute inset-0 bg-[#030712]/30 ${r.id === activeId ? "bg-transparent" : ""}`} />
              </button>
            ))}
          </div>
        </div>

        {/* Story panel */}
        <aside className="col-span-12 lg:col-span-5">
          <div className="glass rounded-sm p-8 md:p-10 h-full flex flex-col" key={active.id}>
            <div className="flex items-center justify-between">
              <p className="overline">{`0${idx + 1} / 0${rooms.length}`}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => go(-1)}
                  data-testid="room-prev"
                  aria-label="Previous room"
                  className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 hover:border-white/40 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  data-testid="room-next"
                  aria-label="Next room"
                  className="w-9 h-9 grid place-items-center rounded-sm border border-white/10 hover:border-white/40 transition-colors"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <Quote className="w-6 h-6 text-[var(--pov-coral)] mt-8" />
            <p className="mt-4 text-white/80 leading-relaxed fade-up">{active.story}</p>

            {active.outcome && active.outcome.length > 0 ? (
              <div className="mt-8 hairline pt-6">
                <p className="overline mb-4">Outcome</p>
                <ul className="space-y-3">
                  {active.outcome.map((o) => (
                    <li
                      key={o}
                      className="flex items-start gap-3 text-sm text-white/75"
                      data-testid={`room-outcome-${active.id}-${o.slice(0, 10)}`}
                    >
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--pov-cyan)] shrink-0" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {active.tag ? <p className="mt-auto pt-8 text-xs text-white/40">{active.tag}</p> : null}
          </div>
        </aside>
      </div>
    </section>
  );
}
